import {DataTable, Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow, DataTableSortState} from '@carbon/react';
import {observer} from 'mobx-react';
import {memo} from 'react';
import VirtualizedDataTable from '../../../../components/virtualized-data-table';
import {ProviderTableToolbar} from './provider-table-toolbar';
import {ProviderTableRow} from './provider-table-row';
import {TableHeaderView} from './table-header-view';
import {useProviderTableLayout} from './hooks/use-provider-table-layout';
import {TableHeader as ExtendedTableHeader} from './types';
import type NetworkOptimizationStore from '../../lib/store';

interface ProviderTableContentProps {
    visibleColumns: string[];
    filteredHeaders: any[];
    editColumnsButton: React.ReactNode;
    columnModalOpen: boolean;
    tableRows: any[];
    sortColumn: string;
    sortDirection: DataTableSortState;
    handleSort: (header: any) => () => void;
    tableHeaders: ExtendedTableHeader[];
    filters: any[];
    selection: string[];
    deselection: string[];
    revisionId: number;
    networkOptimizationStore: NetworkOptimizationStore;
    setSaveRosterModalOpen: (open: boolean) => void;
    existingProviderIds: Set<string>;
    selectionSet: Set<string>;
    deselectionSet: Set<string>;
    handleCheckboxChange: (rowId: string, checked: boolean, isExisting: boolean, onError: () => void) => void;
    setModalMessage: (message: string) => void;
    setModalOpen: (open: boolean) => void;
    scrollToExistingProvider: string | null;
    setFilterHeader: (header: {key: string, header: string, type?: string} | undefined) => void;
    specialtyFilter: string | null;
}

const ProviderTableContent = memo(observer(({
    visibleColumns,
    filteredHeaders,
    editColumnsButton,
    columnModalOpen,
    tableRows,
    sortColumn,
    sortDirection,
    handleSort,
    tableHeaders,
    filters,
    selection,
    deselection,
    revisionId,
    networkOptimizationStore,
    setSaveRosterModalOpen,
    existingProviderIds,
    selectionSet,
    deselectionSet,
    handleCheckboxChange,
    setModalMessage,
    setModalOpen,
    scrollToExistingProvider,
    setFilterHeader,
    specialtyFilter
}: ProviderTableContentProps) => {
    const {
        theadHeight,
        tableWrapperRef,
        cacheKey,
        onVirtualizedTableReady
    } = useProviderTableLayout({
        visibleColumns,
        modalOpen: columnModalOpen,
        sortColumn,
        sortDirection
    });

    return (
        <DataTable rows={tableRows} headers={filteredHeaders} size='sm' isSortable stickyHeader>
            {({
                rows,
                getHeaderProps,
                getRowProps,
                getTableProps,
                getTableContainerProps,
                getToolbarProps,
                getBatchActionProps,
            }) => (
                <TableContainer
                    title={
                        <div className='flex items-center justify-between font-semibold mt-[-1.25rem] mb-[-.75rem]'>
                            <span>
                                Available Providers/Facilities{specialtyFilter && <> - {specialtyFilter}</>}
                            </span>
                            {editColumnsButton}
                        </div>
                    }
                    {...getTableContainerProps()}
                >
                    <ProviderTableToolbar
                        filters={filters}
                        selection={selection}
                        deselection={deselection}
                        revisionId={revisionId}
                        toolbarProps={getToolbarProps()}
                        batchActionProps={getBatchActionProps()}
                        onFilterRemove={(filter) => networkOptimizationStore.filters = filters.filter((f: any) => f !== filter)}
                        onCancelSelection={() => {
                            networkOptimizationStore.deselectAllAdditionalProviders();
                            networkOptimizationStore.selectAllExistingProviders();
                        }}
                        onSaveRoster={async () => {
                            try {
                                await networkOptimizationStore.updateRoster(new AbortController());
                            } catch (err) {
                                throw new Error('Failed to update roster. Please try again later.');
                            }
                        }}
                        setSaveRosterModalOpen={setSaveRosterModalOpen}
                    />
                    <div ref={tableWrapperRef} style={{display: 'contents'}}>
                        <Table {...getTableProps()} aria-label="sample table" className='!overflow-y-hidden'>
                            <TableHead className='pr-4'>
                                <TableRow>
                                    <TableHeader className='select-col' key='select'>
                                        &nbsp;
                                    </TableHeader>
                                    {filteredHeaders.map((header, i) => {
                                        let headerProps = getHeaderProps({header});
                                        let headerOnClick = handleSort(header);
                                        return <TableHeaderView
                                            key={i}
                                            header={header}
                                            headerProps={{...headerProps, key: header.key, isSortHeader: header.key === sortColumn, sortDirection, onClick: headerOnClick}}
                                            onFilter={(h) => {setFilterHeader(tableHeaders.find(header => h === header.key));}}
                                        />;
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody style={{height: `calc(100% - ${theadHeight}px)`}} className='!overflow-y-hidden'>
                                <div className='h-full'>
                                    <VirtualizedDataTable
                                        rows={rows}
                                        cacheKey={cacheKey}
                                        estimatedRowSize={53}
                                        overscanRowCount={5}
                                        height="100%"
                                        scrollToIndex={
                                            scrollToExistingProvider
                                                ? rows.findIndex((r: any) => r.id === scrollToExistingProvider)
                                                : undefined
                                        }
                                        scrollToAlignment={scrollToExistingProvider ? 'center' : undefined}
                                        onReady={onVirtualizedTableReady}
                                        rowRenderer={({row, key}) => (
                                            <ProviderTableRow
                                                key={key}
                                                row={row}
                                                visibleColumns={visibleColumns}
                                                tableHeaders={tableHeaders}
                                                existingProviderIds={existingProviderIds}
                                                selectionSet={selectionSet}
                                                deselectionSet={deselectionSet}
                                                getRowProps={getRowProps}
                                                onCheckboxChange={handleCheckboxChange}
                                                onLastProviderError={() => {
                                                    setModalMessage('You must have at least one existing provider selected.');
                                                    setModalOpen(true);
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                            </TableBody>
                        </Table>
                    </div>
                </TableContainer>
            )}
        </DataTable>
    );
}));

ProviderTableContent.displayName = 'ProviderTableContent';

export default ProviderTableContent;
